import { useState, type FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const FORM_ID = "fancha-contact-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahfv9boh653ivfvntrg";
const MESSAGE_LIMIT = 500;

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const contactWays = [
  {
    icon: "ri-mail-line",
    title: "邮件联系",
    value: "hello@fancha-archive.example",
    desc: "适合正式的更正说明或内容合作沟通。",
  },
  {
    icon: "ri-time-line",
    title: "回复时间",
    value: "工作日 10:00 - 18:00",
    desc: "通常会在 24 小时内给出第一次回复。",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "当事人通道",
    value: "可申请调整或下架",
    desc: "任何被收录的创作者都可通过本表单提出你的诉求。",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，感谢你的反馈，我们会尽快查看。");
      form.reset();
      setMessageLength(0);
      return;
    }

    formData.delete("contact_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFeedback("提交成功，感谢你的反馈，我们会尽快查看并回复。");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFeedback(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交没有成功，请检查填写内容后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络连接异常，请稍后重试，或直接发送邮件联系我们。");
    }
  };

  const contactJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系反差档案",
      description:
        "提交网红反差的对比线索、内容更正请求或合作咨询，也可以通过邮件与本站取得联系。",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: "反差档案",
        email: "hello@fancha-archive.example",
        url: "https://fancha-archive.example",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fancha-archive.example/" },
        { "@type": "ListItem", position: 2, name: "联系反馈" },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="联系反馈 - 提交网红反差对比线索与内容更正"
        description="通过表单提交你想看的网红反差对比线索、指出内容年份或账号对应的错误，或提出合作与当事人相关诉求，我们会在工作日内尽快回复。"
        keywords="联系反差档案,网红反差线索提交,网红对比内容更正,网红反差合作"
        path="/contact"
        ogType="website"
        jsonLd={contactJsonLd}
      />

      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系反馈" }]} />
            <h1 className="mt-5 max-w-3xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              联系反馈：把你觉得该被看到的那一组告诉我们
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              无论是推荐对比对象、指出年份标注错误，还是作为当事人希望调整内容，都可以通过下面的表单提交。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1.25fr_0.75fr] lg:gap-14">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
              <h2 className="font-heading text-xl font-black text-foreground-950">
                在线提交
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项，留言请控制在 {MESSAGE_LIMIT} 字以内。
              </p>

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                noValidate={false}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className="text-sm text-foreground-800">
                      你的称呼 <span className="text-primary-600">*</span>
                    </span>
                    <input
                      type="text"
                      name="name"
                      required
                      autoComplete="name"
                      placeholder="怎么称呼你"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </label>

                  <label className="flex flex-col gap-2">
                    <span className="text-sm text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </span>
                    <input
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="用于接收回复"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className="text-sm text-foreground-800">
                      反馈类型 <span className="text-primary-600">*</span>
                    </span>
                    <select
                      name="type"
                      required
                      defaultValue=""
                      className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="" disabled>
                        请选择反馈类型
                      </option>
                      <option value="线索提交">线索提交（推荐对比对象）</option>
                      <option value="内容更正">内容更正（年份或对应有误）</option>
                      <option value="当事人诉求">当事人诉求（调整或下架）</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他">其他</option>
                    </select>
                  </label>

                  <label className="flex flex-col gap-2">
                    <span className="text-sm text-foreground-800">涉及对象</span>
                    <input
                      type="text"
                      name="target"
                      placeholder="例如：某位博主的名字或账号"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </label>
                </div>

                <label className="flex flex-col gap-2">
                  <span className="text-sm text-foreground-800">
                    详细说明 <span className="text-primary-600">*</span>
                  </span>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={MESSAGE_LIMIT}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="尽量说明具体年份、平台与你想表达的内容，方便我们核实。"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  ></textarea>
                  <span className="self-end text-xs text-foreground-500">
                    {messageLength} / {MESSAGE_LIMIT}
                  </span>
                </label>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="compact-field-note"
                />

                {status !== "idle" && feedback && (
                  <p
                    role="status"
                    className={`rounded-md border px-4 py-3 text-sm ${
                      status === "error"
                        ? "border-primary-300 bg-primary-50 text-primary-900"
                        : "border-secondary-300 bg-secondary-50 text-secondary-900"
                    }`}
                  >
                    {feedback}
                  </p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-foreground-500">
                    我们仅将你填写的信息用于处理本次反馈。
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-background-400"
                  >
                    <i className="ri-send-plane-line"></i>
                    {status === "submitting" ? "提交中..." : "提交反馈"}
                  </button>
                </div>
              </form>
            </div>

            <aside className="flex flex-col gap-4">
              {contactWays.map((way) => (
                <div
                  key={way.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className={`${way.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {way.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-800">{way.value}</p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-500">{way.desc}</p>
                </div>
              ))}

              <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                  <i className="ri-information-line"></i>
                  提交小提示
                </h3>
                <ul className="mt-3 flex flex-col gap-2 text-sm text-foreground-700">
                  <li>· 推荐对象时，附上平台与账号名会更快被整理。</li>
                  <li>· 内容更正请注明具体是哪一组、哪一年有出入。</li>
                  <li>· 当事人诉求请使用可联系的邮箱，方便我们核对身份。</li>
                </ul>
              </div>
            </aside>
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}